import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { Button, cn, MenuButton, toast } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { AlertTriangle, CircleDollarSign, ClipboardCheck, Clock, MoreHorizontal, RefreshCw, Truck } from "lucide-react";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { refusalMessage } from "../../../api/manufacturing";
import { ReplenishmentClient, replenishmentKeys, type ReplenishmentSummary } from "../../../api/replenishment";
import { NexpimView } from "../../../views";
import { AllMaterialsTab } from "./AllMaterialsTab";
import { ExplainPanel } from "./ExplainPanel";
import { LateTab } from "./LateTab";
import { NegativesTab } from "./NegativesTab";
import { OrdersTab } from "./OrdersTab";
import { formatCad, formatWhole } from "./shared";
import { ToOrderTab } from "./ToOrderTab";

/**
 * The buyer's morning screen.
 *
 * <p>The nightly run has already decided, for every purchased material, how
 * much to order and why. This page is where a person checks that, adjusts it,
 * and turns it into purchase orders — grouped the way they are bought, by
 * supplier or by truck. Around it sit the other things a buyer chases: drafts
 * not yet sent, orders waiting for the boss, orders late or held, and stock the
 * ledger has below zero.</p>
 *
 * <p>Everything reads the latest finished run. Recalculate reruns it in about
 * a second; drafts created here are deducted on the next run, so the page
 * reruns it right after creating them.</p>
 *
 * <p>Built for a keyboard at a desk and a finger on a tablet: every action has
 * a key (press ? to see them) and a target big enough to touch.</p>
 */

export type ReplenishmentTab = "order" | "drafts" | "approval" | "late" | "negatives" | "all";

const TABS: readonly ReplenishmentTab[] = ["order", "drafts", "approval", "late", "negatives", "all"];

export function ReplenishmentPage() {
  const { t } = useTranslation();
  const { http, hasView } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const allowed = hasView(NexpimView.PURCHASING);
  const client = useMemo(() => new ReplenishmentClient(http), [http]);
  const [tab, setTab] = useState<ReplenishmentTab>(() => readTab());
  const [explaining, setExplainingPart] = useState<number | null>(null);
  const [explainingWarehouse, setExplainingWarehouse] = useState<number | null>(null);
  // A material kept at two places is explained at the row's warehouse.
  const setExplaining = (partId: number | null, warehouseId?: number | null) => {
    setExplainingPart(partId);
    setExplainingWarehouse(warehouseId ?? null);
  };
  const [showKeys, setShowKeys] = useState(false);

  const summary = useQuery({
    queryKey: replenishmentKeys.summary,
    queryFn: () => client.summary(),
    enabled: allowed,
    refetchOnWindowFocus: true,
  });

  const recalculate = useMutation({
    mutationFn: () => client.run(),
    onSuccess: (result) => {
      toast.success(
        t("replenishment.run.done", "Recalculated {{count}} materials in {{seconds}} s: {{orders}} to order.", {
          count: result.material_count,
          seconds: (result.duration_ms / 1000).toFixed(1),
          orders: result.order_count,
        }),
      );
      void queryClient.invalidateQueries({ queryKey: replenishmentKeys.all });
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  const choose = useCallback((next: ReplenishmentTab) => {
    setTab(next);
    try {
      window.history.replaceState(null, "", `#${next}`);
    } catch {
      // The hash is a convenience; the page works without it.
    }
  }, []);

  // Page-level keys: 1–6 switch tabs, R recalculates, ? shows the keys. Row
  // keys live in the tabs. Nothing fires while typing in a field.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (isTyping(event) || event.metaKey || event.ctrlKey || event.altKey) return;
      const index = Number(event.key) - 1;
      if (index >= 0 && index < TABS.length) {
        event.preventDefault();
        choose(TABS[index] as ReplenishmentTab);
      } else if (event.key === "r" || event.key === "R") {
        event.preventDefault();
        if (!recalculate.isPending) recalculate.mutate();
      } else if (event.key === "?") {
        event.preventDefault();
        setShowKeys((open) => !open);
      } else if (event.key === "Escape") {
        setExplaining(null);
        setShowKeys(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [choose, recalculate]);

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("navigation.item.replenishment", "Replenishment")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  const data = summary.data;
  return (
    <>
      <PageHeader
        title={t("navigation.item.replenishment", "Replenishment")}
        subtitle={runLine(data, t)}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="secondary" loading={recalculate.isPending} onClick={() => recalculate.mutate()} title="R">
              <RefreshCw className="size-4" />
              <span className="max-sm:hidden">{t("replenishment.recalculate", "Recalculate")}</span>
            </Button>
            <MenuButton
              label={t("replenishment.more", "More")}
              icon={<MoreHorizontal className="size-4" />}
              items={[
                { label: t("navigation.item.cash-projection", "Cash projection"), onClick: () => void navigate({ to: "/purchasing/cash" }) },
                { label: t("navigation.item.purchasing-reviews", "Reviews"), onClick: () => void navigate({ to: "/purchasing/reviews" }) },
                { label: t("navigation.item.order-units", "Order units"), onClick: () => void navigate({ to: "/purchasing/order-units" }) },
                { label: t("replenishment.keys.title", "Keyboard shortcuts"), onClick: () => setShowKeys(true) },
              ]}
            />
          </div>
        }
      />

      <div className="flex min-h-0 flex-1">
        <div className="@container flex min-h-0 min-w-0 flex-1 flex-col">
          <Headline data={data} loading={summary.isLoading} onChoose={choose} onCash={() => void navigate({ to: "/purchasing/cash" })} />
          <TabBar tab={tab} data={data} onChoose={choose} />
          <div className="flex min-h-0 flex-1 flex-col">
            {tab === "order" && <ToOrderTab summary={data} onExplain={setExplaining} explaining={explaining} />}
            {tab === "drafts" && <OrdersTab status="DRAFT" canApprove={data?.can_approve ?? false} />}
            {tab === "approval" && <OrdersTab status="AWAITING_APPROVAL" canApprove={data?.can_approve ?? false} />}
            {tab === "late" && <LateTab />}
            {tab === "negatives" && <NegativesTab />}
            {tab === "all" && <AllMaterialsTab onExplain={setExplaining} />}
          </div>
        </div>
        <ExplainPanel partId={explaining} warehouseId={explainingWarehouse} onClose={() => setExplaining(null)} />
      </div>

      {showKeys && <KeysCard onClose={() => setShowKeys(false)} />}
    </>
  );
}

function readTab(): ReplenishmentTab {
  try {
    const hash = window.location.hash.replace("#", "") as ReplenishmentTab;
    return TABS.includes(hash) ? hash : "order";
  } catch {
    return "order";
  }
}

/**
 * Whether a key belongs to something else: a field being typed in, or a
 * dialog or menu open over the page, whose own keys must not also switch tabs
 * or tick rows behind it.
 */
export function isTyping(event: KeyboardEvent): boolean {
  const target = event.target as HTMLElement | null;
  if (!target) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target.isContentEditable) return true;
  return target.closest?.('dialog, [role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"]') != null;
}

/** Enter and Space on a focused button or link are that control's own: they press it. */
export function isControl(event: KeyboardEvent): boolean {
  const target = event.target as HTMLElement | null;
  return target?.closest?.('button, a[href], summary, [role="button"], [role="tab"], [role="checkbox"], [role="switch"]') != null;
}

function runLine(data: ReplenishmentSummary | undefined, t: (key: string, fallback: string, options?: Record<string, unknown>) => string): string {
  if (!data) return t("replenishment.subtitle", "What to buy, when, and what it costs.");
  if (!data.replenishment_run_id) return t("replenishment.no-run", "No run yet: press Recalculate.");
  const at = data.finished_at ? new Date(data.finished_at) : null;
  return t("replenishment.run.line", "Planned {{date}} at {{time}} · {{materials}} materials", {
    date: data.as_of_date ?? "",
    time: at ? at.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : "",
    materials: formatWhole(data.material_count),
  });
}

/* ------------------------------------------------------------ headline --- */

/**
 * The five numbers the plan asked for, each a way into the tab that deals with
 * it: in red, trucks due, late orders, to approve, cash out over four weeks.
 */
function Headline({
  data,
  loading,
  onChoose,
  onCash,
}: {
  data: ReplenishmentSummary | undefined;
  loading: boolean;
  onChoose: (tab: ReplenishmentTab) => void;
  onCash: () => void;
}) {
  const { t } = useTranslation();
  const dueTrucks = data?.shared_units.filter((truck) => truck.is_due) ?? [];
  const urgentTrucks = dueTrucks.filter((truck) => truck.is_urgent).length;
  const tiles: Array<{
    key: string;
    icon: ReactNode;
    label: string;
    value: string;
    note: string;
    tone: "risk" | "warn" | "info" | "ok" | "neutral";
    onClick: () => void;
  }> = [
    {
      key: "red",
      icon: <AlertTriangle className="size-4" />,
      label: t("replenishment.kpi.red", "In red"),
      value: formatWhole(data?.red_count),
      note: t("replenishment.kpi.red.note", "{{materials}} to order · {{amount}}", {
        materials: formatWhole(data?.to_order_count),
        amount: formatCad(data?.to_order_amount_cad),
      }),
      tone: (data?.red_count ?? 0) > 0 ? "risk" : "ok",
      onClick: () => onChoose("order"),
    },
    {
      key: "trucks",
      icon: <Truck className="size-4" />,
      label: t("replenishment.kpi.shared", "Trucks and containers due"),
      value: data ? `${String(dueTrucks.length)}/${String(data.shared_units.length)}` : "",
      note:
        urgentTrucks > 0
          ? t("replenishment.kpi.trucks.urgent", "{{count}} urgent: a member is in red", { count: urgentTrucks })
          : data && data.shared_units.length === 0
            ? t("replenishment.kpi.shared.none", "No trucks or containers set up")
            : t("replenishment.kpi.trucks.note", "Loads planned from the latest run"),
      tone: urgentTrucks > 0 ? "risk" : dueTrucks.length > 0 ? "warn" : "neutral",
      onClick: () => onChoose("order"),
    },
    {
      key: "late",
      icon: <Clock className="size-4" />,
      label: t("replenishment.kpi.late", "Late POs"),
      value: formatWhole(data?.late_order_count),
      note:
        (data?.held_alert_count ?? 0) > 0
          ? t("replenishment.kpi.late.held", "{{count}} held too long", { count: data?.held_alert_count ?? 0 })
          : (data?.stale_order_count ?? 0) > 0
            ? t("replenishment.kpi.late.stale", "+{{count}} stale to close", { count: data?.stale_order_count ?? 0 })
            : t("replenishment.kpi.late.note", "Past their promised or required date"),
      tone: (data?.late_order_count ?? 0) > 0 ? "warn" : "ok",
      onClick: () => onChoose("late"),
    },
    {
      key: "approve",
      icon: <ClipboardCheck className="size-4" />,
      label: t("replenishment.kpi.approve", "To approve"),
      value: formatWhole(data?.awaiting_approval_count),
      note: formatCad(data?.awaiting_approval_amount_cad),
      tone: (data?.awaiting_approval_count ?? 0) > 0 ? "info" : "neutral",
      onClick: () => onChoose("approval"),
    },
    {
      key: "cash",
      icon: <CircleDollarSign className="size-4" />,
      label: t("replenishment.kpi.cash", "Cash out · 4 weeks"),
      value: formatCad(data?.cash_out_committed_cad),
      note: t("replenishment.kpi.cash.note", "+{{pending}} pending · +{{suggested}} suggested", {
        pending: formatCad(data?.cash_out_pending_cad),
        suggested: formatCad(data?.cash_out_suggested_cad),
      }),
      tone: "neutral",
      onClick: onCash,
    },
  ];
  const tone = {
    risk: "text-risk",
    warn: "text-warn",
    info: "text-info",
    ok: "text-ok",
    neutral: "text-ink-muted",
  } as const;
  return (
    <div className="grid grid-cols-2 gap-2 border-b border-line bg-surface px-4 py-3 @xl:grid-cols-3 @4xl:grid-cols-5 lg:px-6">
      {tiles.map((tile) => (
        <button
          key={tile.key}
          type="button"
          onClick={tile.onClick}
          className="group flex min-h-[4.5rem] min-w-0 flex-col items-start gap-1 rounded-lg border border-line-soft bg-surface px-3 py-2.5 text-left transition hover:border-line hover:bg-surface-over focus-visible:outline-2 focus-visible:outline-accent"
        >
          <span className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-ink-faint">
            <span className={tone[tile.tone]}>{tile.icon}</span>
            {tile.label}
          </span>
          <span className={cn("text-[22px] font-semibold leading-none tabular-nums", loading ? "text-ink-faint" : "text-ink")}>
            {loading ? "…" : tile.value}
          </span>
          <span className="w-full truncate text-[12px] text-ink-muted">{loading ? "" : tile.note}</span>
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------- tab bar --- */

function TabBar({
  tab,
  data,
  onChoose,
}: {
  tab: ReplenishmentTab;
  data: ReplenishmentSummary | undefined;
  onChoose: (tab: ReplenishmentTab) => void;
}) {
  const { t } = useTranslation();
  const tabs: Array<{ id: ReplenishmentTab; label: string; count?: number | undefined; tone?: "risk" | "info" }> = [
    { id: "order", label: t("replenishment.tab.order", "To order"), count: data?.to_order_count, tone: "risk" },
    { id: "drafts", label: t("replenishment.tab.drafts", "Drafts"), count: data?.draft_count },
    { id: "approval", label: t("replenishment.tab.approval", "Awaiting approval"), count: data?.awaiting_approval_count, tone: "info" },
    { id: "late", label: t("replenishment.tab.late", "Late & on hold"), count: data?.late_order_count },
    { id: "negatives", label: t("replenishment.tab.negatives", "Negatives"), count: data?.negative_count },
    { id: "all", label: t("replenishment.tab.all", "All materials"), count: data?.material_count },
  ];
  return (
    <div role="tablist" className="flex gap-1 overflow-x-auto border-b border-line bg-surface px-2 lg:px-4">
      {tabs.map((item, index) => (
        <button
          key={item.id}
          type="button"
          role="tab"
          aria-selected={item.id === tab}
          title={String(index + 1)}
          onClick={() => onChoose(item.id)}
          className={cn(
            "flex min-h-11 shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-3 text-[13px]",
            item.id === tab ? "border-accent font-medium text-accent" : "border-transparent text-ink-muted hover:text-ink",
          )}
        >
          {item.label}
          {item.count !== undefined && item.count > 0 && (
            <span
              className={cn(
                "rounded-full px-1.5 py-px text-[11px] font-semibold tabular-nums",
                item.id === tab && item.tone === "risk"
                  ? "bg-risk-soft text-risk"
                  : item.id === tab && item.tone === "info"
                    ? "bg-info-soft text-info"
                    : "bg-surface-sunk text-ink-muted",
              )}
            >
              {formatWhole(item.count)}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------- keys --- */

function KeysCard({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const keys: Array<[string, string]> = [
    ["1 – 6", t("replenishment.keys.tabs", "Switch tab")],
    ["R", t("replenishment.keys.recalculate", "Recalculate")],
    ["/", t("replenishment.keys.search", "Search")],
    ["↑ ↓  j k", t("replenishment.keys.move", "Move between materials")],
    ["Space  x", t("replenishment.keys.select", "Select the material")],
    ["Enter  e", t("replenishment.keys.edit", "Edit its quantity (Enter again snaps to the bundle)")],
    ["i", t("replenishment.keys.explain", "Explain the suggestion")],
    ["g", t("replenishment.keys.group", "Select its whole supplier or truck")],
    ["d", t("replenishment.keys.draft", "Create draft orders from the selection")],
    ["Esc", t("replenishment.keys.close", "Close")],
  ];
  return (
    <div className="fixed inset-x-4 bottom-4 z-40 mx-auto max-w-md rounded-xl border border-line bg-surface-over p-4 shadow-overlay sm:inset-x-auto sm:right-6">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-[13px] font-semibold text-ink">{t("replenishment.keys.title", "Keyboard shortcuts")}</h2>
        <Button variant="ghost" size="sm" onClick={onClose}>
          {t("dialog.close", "Close")}
        </Button>
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-[12px]">
        {keys.map(([key, label]) => (
          <div key={key} className="contents">
            <dt>
              <kbd className="rounded border border-line bg-surface-sunk px-1.5 py-0.5 font-mono text-[11px] text-ink">{key}</kbd>
            </dt>
            <dd className="text-ink-muted">{label}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
